<script setup lang="ts">

import { computed, toRef, ref } from 'vue'
import { useField } from 'vee-validate'
import { id as id2 } from 'random-html-id'
import { storeToRefs } from 'pinia'

export interface Props {
  label?: string
  name: string
  id?: string
  autocomplete?: string
  prefix?: string
  suffix?: string
  help?: string
  placeholder?: string
  autofocus?: boolean
  readonly?: boolean
  disabled?: boolean
  rules?: string
  tooltip?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  autocomplete: 'off',
  prefix: '',
  id: '',
  suffix: '',
  help: '',
  placeholder: '',
  autofocus: false,
  readonly: false,
  disabled: false,
  rules: '',
  tooltip: ''
})

const emit = defineEmits(['input'])

const name = toRef(props, 'name')
const rules = toRef(props, 'rules')
const label = toRef(props, 'label')

const { value, errorMessage, meta } = useField(name, rules, { label })

const htmlId = computed(() => props.id ? props.id : `input-${id2()}`)
const hasError = computed(() => meta.touched && errorMessage.value !== undefined)

const inputRef = ref<HTMLElement | null>(null)

</script>

<template>
  <div class="border-0 p-0">
    <template v-if="label">
      <div class="flex-1 flex items-center">
        <div class="flex-1">
          <storm-ui-label
            v-if="label"
            :label="label"
            :required="rules?.includes('required')"
          />
        </div>
      </div>
    </template>
    <div class="w-full">
      <input
        :id="htmlId"
        ref="inputRef"
        v-model="value"
        class="w-full px-2 py-2 border-gray-300 text-base focus:border-blue-400 focus:ring-blue-200 text-black block rounded font-sans disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 focus:ring focus:ring-opacity-50 placeholder:text-gray-400"
        :class="
          [
            'font-sans',
            disabled ? 'bg-gray-50' : '',
            hasError ? ' focus:border-red-400 bg-red-50  focus:ring-red-200 border-red-300' : 'border-gray-300 focus:border-blue-400  focus:ring-blue-200',
          ]
        "
        :disabled="disabled"
        :autocomplete="autocomplete"
        :autofocus="autofocus"
        :placeholder="placeholder"
        passwordrules="minlength: 12; required: lower; required: upper; required: digit; required: [!@#$%^&*+=.-];"
        :name="name"
        type="password"
        @input="emit('input', $event.target.value)"
      >
    </div>
  </div>
</template>
